import React, { useRef, useEffect, useState } from 'react';
import { Copy, Download, RefreshCw } from 'lucide-react';
import Editor, { OnMount } from '@monaco-editor/react';

interface CodeEditorProps {
  code: string;
  codeType: 'react' | 'nodejs';
  onChange: (code: string) => void;
  onRun: () => void;
}

export default function CodeEditor({ code, codeType, onChange, onRun }: CodeEditorProps) {
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);

  const handleEditorDidMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    setIsEditorReady(true);
    
    // Add keyboard shortcut for running code
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
      onRun();
    });

    // Force a layout update after a short delay
    setTimeout(() => {
      editor.layout();
    }, 100);
  };

  // Force editor layout when component mounts or dimensions change
  useEffect(() => {
    if (isEditorReady && editorRef.current) {
      const timer = setTimeout(() => {
        editorRef.current?.layout();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isEditorReady]);

  const copyCode = async () => {
    try {
      const selectedText = editorRef.current?.getSelection()?.toString();
      const textToCopy = selectedText || code;
      await navigator.clipboard.writeText(textToCopy);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const downloadCode = () => {
    const blob = new Blob([code], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = codeType === 'nodejs' ? 'server.js' : 'component.jsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getLanguage = () => {
    return codeType === 'nodejs' ? 'javascript' : 'javascript';
  };

  const getTheme = () => {
    return 'vs-dark';
  };

  const getOptions = () => {
    return {
      minimap: { enabled: false },
      fontSize: 14,
      lineNumbers: 'on',
      roundedSelection: false,
      scrollBeyondLastLine: false,
      readOnly: false,
      automaticLayout: true,
      wordWrap: 'on',
      tabSize: 2,
      insertSpaces: true,
      detectIndentation: false,
      trimAutoWhitespace: true,
      largeFileOptimizations: false,
      suggest: {
        showKeywords: true,
        showSnippets: true,
        showClasses: true,
        showFunctions: true,
        showVariables: true,
        showModules: true,
        showProperties: true,
        showEvents: true,
        showOperators: true,
        showUnits: true,
        showValues: true,
        showConstants: true,
        showEnums: true,
        showEnumMembers: true,
        showColors: true,
        showFiles: true,
        showReferences: true,
        showFolders: true,
        showTypeParameters: true,
        showWords: true,
        showUsers: true,
        showIssues: true,
      },
      quickSuggestions: {
        other: true,
        comments: true,
        strings: true,
      },
      parameterHints: {
        enabled: true,
      },
      hover: {
        enabled: true,
      },
      contextmenu: true,
      mouseWheelZoom: true,
      bracketPairColorization: {
        enabled: true,
      },
      guides: {
        bracketPairs: true,
        indentation: true,
        highlightActiveIndentation: true,
      },
    };
  };

  return (
    <div className="flex flex-col h-full bg-gray-900 border-r border-gray-700" style={{ minHeight: '400px' }}>
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <h2 className="text-sm font-medium text-gray-200">Editor</h2>
          <div className={`px-2 py-1 rounded text-xs font-medium ${
            codeType === 'nodejs' 
              ? 'bg-green-800 text-green-200' 
              : 'bg-blue-800 text-blue-200'
          }`}>
            {codeType === 'nodejs' ? 'Node.js' : 'React'}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={copyCode}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
            title="Copy code"
          >
            <Copy size={16} />
          </button>
          <button
            onClick={downloadCode}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
            title="Download code"
          >
            <Download size={16} />
          </button>
          <button
            onClick={onRun}
            className={`px-3 py-2 text-white text-sm font-medium rounded-md transition-colors flex items-center space-x-2 ${
              codeType === 'nodejs'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
            title="Run code (Cmd/Ctrl + Enter)"
          >
            <RefreshCw size={14} />
            <span>Run</span>
          </button>
        </div>
      </div>
      
      <div className="flex-1" style={{ minHeight: '300px', position: 'relative' }}>
        <Editor
          height="100%"
          width="100%"
          language={getLanguage()}
          theme={getTheme()}
          value={code}
          onChange={(value) => onChange(value || '')}
          onMount={handleEditorDidMount}
          options={getOptions()}
          loading={<div className="flex items-center justify-center h-full text-gray-400">Loading editor...</div>}
        />
      </div>
      
      <div className="px-4 py-2 bg-gray-800 border-t border-gray-700 text-xs text-gray-400">
        Press Cmd/Ctrl + Enter to run • Tab for indentation • Full syntax highlighting and autocomplete
      </div>
    </div>
  );
}